import { graphql } from 'gatsby';
import { GatsbyImage, getSrc, IGatsbyImageData } from 'gatsby-plugin-image';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import React from 'react';

import { ImageCaption } from '../ImageCaption';
import ImageRow from '../ImageRow';
import {
  imageRow,
  imageRow2Up,
  imageRow3Up,
  imageRow4Up,
} from './RichTextGallery.module.scss';

const GalleryImage: React.FC<{
  image: {
    readonly id: string;
    readonly title: string;
    readonly description: string;
    readonly gatsbyImageData: IGatsbyImageData;
  };
}> = ({ image }) => {
  const hasCaption = get(image, 'metadata.tags', []).some(
    (tag) =>
      tag.contentful_id === 'hasCaption' || tag.contentful_id === 'needsCredit',
  );

  return (
    <figure>
      <GatsbyImage
        key={getSrc(image.gatsbyImageData)}
        image={image.gatsbyImageData}
        alt={image.description || image.title}
      />
      {hasCaption && isString(image.description) ? (
        <ImageCaption caption={image.description} />
      ) : null}
    </figure>
  );
};

const RichTextGallery: React.FC<{
  node: { data: { target: Queries.RichTextGalleryFragment } };
}> = ({ node }) => {
  const entry = get(node, 'data.target');

  if (!entry) {
    console.warn('[RichTextGallery] Entry not defined', { node });
    return null;
  }

  if (!isArray(entry.images) || entry.images.length < 1) {
    console.warn('[RichTextGallery] Entry not rendered', { node });
    return null;
  }

  const images = entry.images.filter((image) =>
    isObject(image.gatsbyImageData),
  );

  if (images.length === 2) {
    return (
      <div className={imageRow2Up}>
        {images.map((image) => (
          <GalleryImage key={getSrc(image.gatsbyImageData)} {...{ image }} />
        ))}
      </div>
    );
  }

  if (images.length === 3) {
    return (
      <div className={imageRow3Up}>
        {images.map((image) => (
          <GalleryImage key={getSrc(image.gatsbyImageData)} {...{ image }} />
        ))}
      </div>
    );
  }

  if (images.length === 4) {
    return (
      <div className={imageRow4Up}>
        {images.map((image) => (
          <GalleryImage key={getSrc(image.gatsbyImageData)} {...{ image }} />
        ))}
      </div>
    );
  }

  return (
    <div className={imageRow}>
      <ImageRow images={images} />
    </div>
  );
};

export default RichTextGallery;

export const query = graphql`
  fragment RichTextGallery on ContentfulComponentGallery {
    __typename
    contentful_id
    images {
      id
      title
      description
      gatsbyImageData(
        formats: [AUTO, AVIF, WEBP]
        quality: 80
        width: 1280
        placeholder: BLURRED
      )
      metadata {
        tags {
          contentful_id
        }
      }
    }
  }
`;
